import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Check } from "lucide-react";

const PHASES = [
  { id: "antes", label: "Antes del evento" },
  { id: "durante", label: "Durante el evento" },
  { id: "despues", label: "Después del evento" },
];

const PRIORITY_COLOR = {
  Alta: "text-red-700 border-red-200 bg-red-50",
  Media: "text-amber-800 border-amber-200 bg-amber-50",
  Baja: "text-stone-700 border-stone-200 bg-stone-50",
};

export default function StaffChecklist() {
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("antes");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/checklist").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => items.filter(i => i.phase === phase), [items, phase]);

  const toggle = async (it) => {
    const next = !it.done;
    setItems(items.map(x => x.id === it.id ? { ...x, done: next, status: next ? "Completado" : "Pendiente" } : x));
    try { await api.patch(`/checklist/${it.id}`, { done: next }); } catch {}
  };

  return (
    <div data-testid="staff-checklist" className="space-y-6">
      <div>
        <div className="overline text-[var(--brand-primary)]">Checklist operativa</div>
        <h2 className="font-serif-cg text-3xl md:text-4xl tracking-tight mt-2">Tareas por fase</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        {PHASES.map(p => (
          <button
            key={p.id}
            data-testid={`checklist-phase-${p.id}`}
            onClick={() => setPhase(p.id)}
            className={`text-xs px-4 py-2 uppercase tracking-wider border ${
              phase === p.id
                ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                : "bg-white border-[var(--brand-border)] hover:border-[var(--brand-primary)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[var(--brand-muted)]">Cargando tareas...</p>
      ) : (
        <ul className="card-cg divide-y divide-[var(--brand-border)]">
          {filtered.map((it, idx) => (
            <li key={it.id} data-testid={`checklist-item-${idx}`} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
              <button
                onClick={() => toggle(it)}
                data-testid={`checklist-toggle-${idx}`}
                className={`shrink-0 w-6 h-6 border flex items-center justify-center transition-colors ${
                  it.done ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : "border-[var(--brand-border)] hover:border-[var(--brand-primary)]"
                }`}
                aria-label={it.done ? "Marcar como pendiente" : "Marcar como hecho"}
              >
                {it.done && <Check size={14} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${it.done ? "line-through text-[var(--brand-muted)]" : ""}`}>{it.task}</div>
                <div className="text-xs text-[var(--brand-muted)] mt-1 flex flex-wrap gap-3">
                  <span>Responsable: {it.responsible}</span>
                  <span>· Plazo: {it.deadline}</span>
                </div>
              </div>
              <span className={`text-[11px] px-2 py-1 border ${PRIORITY_COLOR[it.priority]}`}>Prioridad {it.priority}</span>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="p-6 text-[var(--brand-muted)] text-sm">Sin tareas en esta fase.</li>
          )}
        </ul>
      )}
    </div>
  );
}
